"use client";

import { c as _c } from "react/compiler-runtime";
import { Button } from "@/components/rac/button";
import { DialogContent, DialogOverlay } from "@/components/rac/dialog";
import { SheetContent, SheetOverlay } from "@/components/rac/sheet";
import { SkeletonLoader } from "@/components/ui/skeleton";
import { useHPIData } from "@/hooks/use-hpi-data";
import { useIsMobile } from "@/hooks/use-mobile";
import { ChevronDown, ChevronUp, X } from "lucide-react";
import { useState } from "react";
import { type ComparablePrice } from "../optimization/types";
interface ModalContentProps {
  comparablePrices?: ComparablePrice[];
  onClose: () => void;
  RBI_PRICE_INDEX_DATA: ReturnType<typeof useHPIData>["data"];
  isHPITableExpanded: boolean;
  setIsHPITableExpanded: (value: boolean) => void;
}
const ModalContent = t0 => {
  const $ = _c(41);
  const {
    comparablePrices,
    onClose,
    RBI_PRICE_INDEX_DATA,
    isHPITableExpanded,
    setIsHPITableExpanded
  } = t0;
  const soldQuarter = comparablePrices?.[0]?.indexMultiplierKey || "2010-1";
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[0] !== RBI_PRICE_INDEX_DATA || $[1] !== comparablePrices?.[0]?.steps || $[2] !== isHPITableExpanded || $[3] !== onClose || $[4] !== setIsHPITableExpanded || $[5] !== soldQuarter) {
    const soldIndex = RBI_PRICE_INDEX_DATA?.findIndex(data => data.quarter === soldQuarter) ?? 0;
    let t7;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <h2 className="text-xl font-semibold">Price Adjustment</h2>;
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    let t8;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <X className="h-5 w-5 text-gray-500" />;
      $[13] = t8;
    } else {
      t8 = $[13];
    }
    if ($[14] !== onClose) {
      t4 = <div className="flex justify-between items-center mb-4">{t7}<Button variant="bareshell" onPress={onClose} className="p-1">{t8}</Button></div>;
      $[14] = onClose;
      $[15] = t4;
    } else {
      t4 = $[15];
    }
    if ($[16] !== comparablePrices?.[0]?.steps) {
      t5 = comparablePrices?.[0]?.steps.map(_temp);
      $[16] = comparablePrices?.[0]?.steps;
      $[17] = t5;
    } else {
      t5 = $[17];
    }
    let t9;
    if ($[18] !== isHPITableExpanded || $[19] !== setIsHPITableExpanded) {
      t9 = () => setIsHPITableExpanded(!isHPITableExpanded);
      $[18] = isHPITableExpanded;
      $[19] = setIsHPITableExpanded;
      $[20] = t9;
    } else {
      t9 = $[20];
    }
    const t10 = isHPITableExpanded ? "Hide HPI details" : "Show HPI details";
    let t11;
    if ($[21] !== isHPITableExpanded) {
      t11 = isHPITableExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />;
      $[21] = isHPITableExpanded;
      $[22] = t11;
    } else {
      t11 = $[22];
    }
    const t12 = isHPITableExpanded ? "Hide" : "View";
    let t13;
    if ($[23] !== t12) {
      t13 = <span>{t12} Historical Price Index</span>;
      $[23] = t12;
      $[24] = t13;
    } else {
      t13 = $[24];
    }
    if ($[25] !== t10 || $[26] !== t11 || $[27] !== t13 || $[28] !== t9) {
      t6 = <div className="mt-2 space-y-3"><div className="flex justify-center"><button onClick={t9} className="inline-flex items-center gap-1.5 px-4 py-2 text-xs sm:text-sm text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50 font-medium transition-all cursor-pointer rounded-lg border border-emerald-200 shadow-sm hover:shadow-md" aria-label={t10}>{t11}{t13}</button></div></div>;
      $[25] = t10;
      $[26] = t11;
      $[27] = t13;
      $[28] = t9;
      $[29] = t6;
    } else {
      t6 = $[29];
    }
    t3 = `overflow-hidden transition-all duration-300 ease-in-out ${isHPITableExpanded ? "max-h-[400px] opacity-100 mt-4" : "max-h-0 opacity-0 mt-0"}`;
    t1 = "pt-4 border-t border-emerald-200";
    t2 = RBI_PRICE_INDEX_DATA && <div className="max-h-64 overflow-y-auto border border-gray-200 rounded"><table className="w-full text-xs"><thead className="bg-gray-50 sticky top-0"><tr><th className="text-left px-3 py-2 font-medium text-gray-700 border-b">Quarter</th><th className="text-right px-3 py-2 font-medium text-gray-700 border-b">Index</th></tr></thead><tbody>{RBI_PRICE_INDEX_DATA.slice(soldIndex).map((data_0, index) => {
            const slicedLength = RBI_PRICE_INDEX_DATA.slice(soldIndex).length;
            return <tr key={data_0.quarter} className={index === 0 || index === slicedLength - 1 ? "bg-emerald-50 font-medium" : index % 2 === 0 ? "bg-white" : "bg-gray-50"}><td className="px-3 py-2 border-b text-gray-900">{data_0.quarter}{index === slicedLength - 1 && <span className="ml-1 text-emerald-600">(Latest)</span>}{index === 0 && <span className="ml-1 text-blue-600">(Sold)</span>}</td><td className="px-3 py-2 border-b text-right text-gray-900">{data_0.value}</td></tr>;
          })}</tbody></table></div>;
    $[0] = RBI_PRICE_INDEX_DATA;
    $[1] = comparablePrices?.[0]?.steps;
    $[2] = isHPITableExpanded;
    $[3] = onClose;
    $[4] = setIsHPITableExpanded;
    $[5] = soldQuarter;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t1 = $[6];
    t2 = $[7];
    t3 = $[8];
    t4 = $[9];
    t5 = $[10];
    t6 = $[11];
  }
  let t7;
  if ($[30] !== t1 || $[31] !== t2) {
    t7 = <div className={t1}>{t2}</div>;
    $[30] = t1;
    $[31] = t2;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  let t8;
  if ($[33] !== t3 || $[34] !== t7) {
    t8 = <div className={t3}>{t7}</div>;
    $[33] = t3;
    $[34] = t7;
    $[35] = t8;
  } else {
    t8 = $[35];
  }
  let t9;
  if ($[36] !== t4 || $[37] !== t5 || $[38] !== t6 || $[39] !== t8) {
    t9 = <div>{t4}{t5}{t6}{t8}</div>;
    $[36] = t4;
    $[37] = t5;
    $[38] = t6;
    $[39] = t8;
    $[40] = t9;
  } else {
    t9 = $[40];
  }
  return t9;
};
interface HPIInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  comparablePrices?: ComparablePrice[];
}
export function HPIInfoModal(t0) {
  const $ = _c(22);
  const {
    isOpen,
    onClose,
    comparablePrices
  } = t0;
  const [isHPITableExpanded, setIsHPITableExpanded] = useState(false);
  const isMobile = useIsMobile();
  const {
    data: RBI_PRICE_INDEX_DATA,
    loading
  } = useHPIData(!isOpen);
  if (!isOpen) {
    return null;
  }
  let t1;
  if ($[0] !== RBI_PRICE_INDEX_DATA || $[1] !== comparablePrices || $[2] !== isHPITableExpanded || $[3] !== loading || $[4] !== onClose) {
    t1 = loading ? <div className="flex items-center justify-center py-12"><SkeletonLoader height="200px" width="100%" /></div> : <ModalContent comparablePrices={comparablePrices} onClose={onClose} RBI_PRICE_INDEX_DATA={RBI_PRICE_INDEX_DATA} isHPITableExpanded={isHPITableExpanded} setIsHPITableExpanded={setIsHPITableExpanded} />;
    $[0] = RBI_PRICE_INDEX_DATA;
    $[1] = comparablePrices;
    $[2] = isHPITableExpanded;
    $[3] = loading;
    $[4] = onClose;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const modalContentOrLoading = t1;
  if (isMobile) {
    let t2;
    if ($[6] !== onClose) {
      t2 = open => !open && onClose();
      $[6] = onClose;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    let t3;
    if ($[8] !== modalContentOrLoading) {
      t3 = <SheetContent side="bottom" className="max-h-[90vh] overflow-y-auto rounded-t-2xl p-4" closeButton={false}>{modalContentOrLoading}</SheetContent>;
      $[8] = modalContentOrLoading;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    let t4;
    if ($[10] !== isOpen || $[11] !== t2 || $[12] !== t3) {
      t4 = <SheetOverlay isOpen={isOpen} onOpenChange={t2}>{t3}</SheetOverlay>;
      $[10] = isOpen;
      $[11] = t2;
      $[12] = t3;
      $[13] = t4;
    } else {
      t4 = $[13];
    }
    return t4;
  }
  let t2;
  if ($[14] !== onClose) {
    t2 = open_0 => !open_0 && onClose();
    $[14] = onClose;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  let t3;
  if ($[16] !== modalContentOrLoading) {
    t3 = <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" closeButton={false}>{modalContentOrLoading}</DialogContent>;
    $[16] = modalContentOrLoading;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  let t4;
  if ($[18] !== isOpen || $[19] !== t2 || $[20] !== t3) {
    t4 = <DialogOverlay isOpen={isOpen} onOpenChange={t2}>{t3}</DialogOverlay>;
    $[18] = isOpen;
    $[19] = t2;
    $[20] = t3;
    $[21] = t4;
  } else {
    t4 = $[21];
  }
  return t4;
}
function _temp(step) {
  return <li key={step}>{step}</li>;
}